import { Menu, Transition } from "@headlessui/react";
import { Bars4Icon } from "@heroicons/react/24/outline";
import { Fragment } from "react";
import ClientLogo from "img/client-logo.webp";
import storage from "app/storage";

import { TopBarScannerIndicator } from "app/components/molecules/barcodeScanner/TopBarScannerIndicator";

const profileNavigation = [
  { name: "Profil", href: "/profile", role: "Admin" },
  { name: "Logout", href: "/logout", role: "Admin" },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function parseJwt(token) {
  var base64Url = token.split(".")[1];
  var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  var jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
}

/**
 * Komponen Navbar Utama aplikasi.
 * Menampilkan tombol toggle sidebar untuk mobile, indikator status global barcode scanner,
 * serta menu dropdown profil pengguna di sisi kanan header.
 *
 * @param {Object} props - Properti komponen.
 * @param {Function} props.onSidebarButtonClick - Callback saat tombol toggle sidebar mobile diklik.
 * @returns {JSX.Element} Elemen navbar utama dengan posisi responsif terhadap sidebar desktop.
 * @sideEffects Memvalidasi masa berlaku token JWT dan redirect ke /logout jika token kedaluwarsa.
 */
function MainLayoutNavbar({ onSidebarButtonClick }) {
  const currentTime = new Date().getTime();

  const token = storage.authentication.token();
  const { email, token_expire_date } = parseJwt(token);

  if (token_expire_date > currentTime) {
    // do nothing
  } else {
    console.log("Token no longer valid!", token_expire_date, currentTime);
    window.location.href = "/logout";
  }

  return (
    <div className="flex flex-1 flex-col justify-between print:hidden">
      <div className="sticky top-0 z-30 flex items-center justify-between w-full h-16 flex-shrink-0 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 md:bg-white md:dark:bg-gray-800 md:pl-14 xl:pl-52">
        {/* Sisi Kiri: Tombol Buka Sidebar untuk Layar Mobile */}
        <div className="flex items-center">
          <button data-testid="navbar-toggle-sidebar-button"
            type="button"
            className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:border-gray-700 md:hidden"
            onClick={() => onSidebarButtonClick()}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars4Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Sisi Kanan: Status Indikator Barcode Scanner & Menu Profil Pengguna */}
        <div className="flex items-center gap-2 sm:gap-3 mr-2 sm:mr-4 flex-shrink-0">
          {/* Global Barcode Scanner Status Indicator */}
          <TopBarScannerIndicator />

          {/* Profile dropdown */}
          <Menu as="div" className="relative flex-shrink-0">
            <div className="px-1 sm:px-2">
              <Menu.Button className="flex h-14 max-w-xs items-center gap-1 sm:gap-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2">
                <span className="sr-only">Open menu</span>
                <img
                  className="h-10 sm:h-14 w-16 sm:w-24 ml-1 sm:ml-2 bg-white object-contain text-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-600 flex-shrink-0"
                  src={ClientLogo}
                  alt=""
                />
                <div className="hidden sm:flex flex-col px-2 text-left min-w-0">
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate max-w-[130px]">
                    {process.env.REACT_APP_CLIENT_NAME}
                  </span>
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400 truncate max-w-[130px]">
                    {email}
                  </span>
                </div>
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 top-14 z-50 w-32 origin-top-right rounded-md bg-white py-1 ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 md:w-48">
                {profileNavigation.map((item) => (
                  <Menu.Item key={item.name}>
                    {({ active }) => (
                      <a
                        href={item.href}
                        className={classNames(
                          active ? "bg-gray-100 dark:bg-gray-800" : "",
                          "block px-4 py-2 text-sm text-gray-700 dark:text-gray-200"
                        )}
                      >
                        {item.name}
                      </a>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  );
}

export default MainLayoutNavbar;
